import { useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { Check, Clock, X } from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { Avatar } from "@/components/tuw/Avatar"
import NotifIcon from "@/components/tuw/notifications/NotifIcon"
import { csrfToken } from "@/lib/orderActions"
import { cn } from "@/lib/utils"

// Atrium item 6 — the full customer Notifications page (the bell feed). Renders
// the presenter's BOUNDED recent window (rows) with window-scoped filter counts
// (PR-001), so each chip number equals the rows that filter shows. Every write is
// a session action that redirects back here, so the page is always server-truth
// (no optimistic client state): "Mark all read" is non-destructive (sets read_at),
// per-row dismiss is a destructive clear. Filtering + Today/Earlier grouping are
// client-side over the already-loaded window.

// Filter chips in display order. `key` matches the presenter's `counts` keys and
// each row's `category`; `tag` is the per-row category pill label.
const FILTERS = [
  { key: "all", label: "All" },
  { key: "approvals", label: "Approvals", tag: "Approval" },
  { key: "updates", label: "Project updates", tag: "Update" },
  { key: "requests", label: "Requests", tag: "Request" },
  { key: "billing", label: "Billing", tag: "Billing" },
]

const TAG_BY_CATEGORY = FILTERS.reduce((acc, filter) => {
  if (filter.tag) acc[filter.key] = filter.tag
  return acc
}, {})

function FilterChip({ filter, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-[7px] rounded-full border px-[13px] py-[7px] text-[12.5px] font-semibold transition-colors",
        active
          ? "border-foreground bg-foreground text-background"
          : "border-border bg-card text-muted-foreground hover:border-ink-3"
      )}
    >
      {filter.label}
      <span
        className={cn(
          "rounded-[10px] px-[7px] py-px text-[11px] font-bold",
          active ? "bg-white/20 text-white" : "bg-secondary text-ink-3"
        )}
      >
        {count}
      </span>
    </button>
  )
}

function NotificationRow({ notification, onDismiss }) {
  const { unread } = notification

  return (
    <div
      className={cn(
        "group relative mb-[9px] flex items-start gap-[14px] rounded-[14px] border bg-card px-4 py-[15px] transition-colors",
        unread
          ? "border-[color-mix(in_oklab,var(--primary)_24%,var(--border))]"
          : "border-border opacity-[0.82]"
      )}
    >
      {unread && (
        <span className="absolute top-[14px] bottom-[14px] left-0 w-[3px] rounded-[3px] bg-primary" />
      )}

      <NotifIcon icon={notification.icon} tone={notification.tone} size={40} />

      <div className="min-w-0 flex-1">
        <p className="text-[14px] leading-[1.45] font-medium text-foreground">{notification.title}</p>
        {notification.sub && <p className="mt-[3px] text-[12.5px] leading-[1.5] text-ink-3">{notification.sub}</p>}

        <div className="mt-[10px] flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-[5px] text-[11.5px] font-medium text-ink-3">
            <Clock className="size-[13px]" strokeWidth={2} />
            {notification.time}
          </span>
          {TAG_BY_CATEGORY[notification.category] && (
            <span className="rounded-[6px] bg-secondary px-2 py-[2px] text-[10.5px] font-bold tracking-[0.03em] text-ink-3 uppercase">
              {TAG_BY_CATEGORY[notification.category]}
            </span>
          )}
          {notification.actionLabel && notification.href && (
            <a
              href={notification.href}
              className="inline-flex items-center gap-[6px] rounded-[8px] bg-foreground px-3 py-[6px] text-[12px] font-semibold text-background no-underline transition-colors hover:bg-foreground/90"
            >
              {notification.actionLabel}
            </a>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-2">
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => onDismiss(notification.id)}
          className="grid size-[26px] place-items-center rounded-[7px] text-ink-3 opacity-0 transition-opacity hover:bg-secondary hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100"
        >
          <X className="size-[15px]" strokeWidth={2} />
        </button>
        {notification.who?.n && <Avatar initials={notification.who.n} size={22} />}
      </div>
    </div>
  )
}

function EmptyState({ label }) {
  return (
    <div className="py-[70px] text-center text-ink-3">
      <span className="mx-auto mb-4 grid size-14 place-items-center rounded-[15px] bg-secondary text-ink-3">
        <Check className="size-[26px]" strokeWidth={1.7} />
      </span>
      <h3 className="mb-[5px] font-display text-base font-semibold text-muted-foreground">You're all caught up</h3>
      <p className="mx-auto max-w-[34ch] text-[13px]">
        No {label ? `${label.toLowerCase()} ` : ""}notifications right now.
      </p>
    </div>
  )
}

export default function Notifications({ notifications }) {
  const [active, setActive] = useState("all")

  const rows = notifications?.rows ?? []
  const counts = notifications?.counts ?? { all: 0, approvals: 0, updates: 0, requests: 0, billing: 0 }
  const anyUnread = notifications?.unread > 0

  const visible = useMemo(
    () => (active === "all" ? rows : rows.filter((row) => row.category === active)),
    [rows, active]
  )
  const today = visible.filter((row) => row.group === "today")
  const earlier = visible.filter((row) => row.group === "earlier")

  const headers = { "X-CSRF-Token": csrfToken() }

  const markAll = () =>
    router.post("/notifications/mark_all_read", {}, { headers, preserveScroll: true })

  const dismiss = (id) =>
    router.post(`/notifications/${id}/dismiss`, {}, { headers, preserveScroll: true })

  const activeLabel = FILTERS.find((filter) => filter.key === active)?.label

  const section = (label, list) =>
    list.length > 0 && (
      <>
        <div className="mx-1 mt-[22px] mb-[9px] text-[11px] font-bold tracking-[0.07em] text-ink-3 uppercase first:mt-1">
          {label}
        </div>
        {list.map((row) => (
          <NotificationRow key={row.id} notification={row} onDismiss={dismiss} />
        ))}
      </>
    )

  return (
    <>
      <div className="mx-auto w-full max-w-[780px] px-6 pt-[26px] pb-[60px]">
        <div className="mb-[18px] flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-[25px] font-semibold tracking-[-0.01em] text-foreground">Notifications</h1>
            <p className="mt-[3px] max-w-[46ch] text-[13px] text-ink-3">
              Approvals, project milestones, requests and billing — everything your projects need from you, in one
              place.
            </p>
          </div>
          <button
            type="button"
            onClick={markAll}
            disabled={!anyUnread}
            className="inline-flex shrink-0 items-center gap-[6px] rounded-[9px] border border-border bg-card px-3 py-2 text-[12.5px] font-semibold whitespace-nowrap text-muted-foreground transition-colors hover:border-ink-3 hover:text-foreground disabled:cursor-default disabled:opacity-[0.45] disabled:hover:border-border disabled:hover:text-muted-foreground"
          >
            <Check className="size-[15px]" strokeWidth={2} />
            Mark all read
          </button>
        </div>

        <div className="mb-5 flex flex-wrap gap-[7px]">
          {FILTERS.map((filter) => (
            <FilterChip
              key={filter.key}
              filter={filter}
              count={counts[filter.key] ?? 0}
              active={active === filter.key}
              onClick={() => setActive(filter.key)}
            />
          ))}
        </div>

        <div>
          {visible.length > 0 ? (
            <>
              {section("Today", today)}
              {section("Earlier", earlier)}
            </>
          ) : (
            <EmptyState label={active === "all" ? "" : activeLabel} />
          )}
        </div>
      </div>
    </>
  )
}

// No sidebar item highlights (Notifications lives in the topbar bell); crumb only.
Notifications.layout = (page) => (
  <AppShell active="" crumb={["Notifications"]}>
    {page}
  </AppShell>
)
